import { type FC, useContext, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Stack,
  TextField,
  MenuItem,
  type AlertColor,
  type AlertPropsColorOverrides,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import type { Merchant } from '../models/types';
import type { OverridableStringUnion } from '@mui/types';

import { DataContext } from '../contexts/DataContext';
import { createBackendUser, createMerchant } from '../fetchFns';
import useBackdrop from '../hooks/useBackdrop';
import useSnackbar from '../hooks/useSnackbar';
import { supabase } from '../supabase-client';
import useDialog from '../hooks/useDialog';

const userRoles = ['tranding_admin', 'merchant_admin', 'merchant_employee'];

type FormValues = {
  merchant_id: string;
  email: string;
  user_role: 'tranding_admin' | 'merchant_admin' | 'merchant_employee';
  password: string;
};

const defaultValues: FormValues = {
  merchant_id: '',
  email: '',
  user_role: 'merchant_admin',
  password: '',
};

export default function CreateBackendUserDialog({
  getUserData,
}: {
  getUserData: () => Promise<void>;
}) {
  const {
    handleSubmit,
    control,
    reset,
    resetField,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues,
    mode: 'onTouched',
  });
  const dataCtx = useContext(DataContext);
  const backdrop = useBackdrop();
  const dialog = useDialog();
  const snackbar = useSnackbar();
  const [brands, setBrands] = useState<Merchant[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onSubmit = async (data: FormValues) => {
    const formData = new FormData();
    backdrop.handleBackdropOpen();

    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }

    let message = `The user has been created.`;
    let severity:
      | OverridableStringUnion<AlertColor, AlertPropsColorOverrides>
      | undefined = 'success';

    try {
      const response = await createBackendUser(formData);
      reset();
      await getUserData();
      dialog.handleDialogClose();
    } catch (error) {
      message = String(error);
      severity = 'error';
    }
    snackbar.handleSnackbarOpen(message, severity);
    backdrop.handleBackdropClose();
  };

  const getData = async () => {
    backdrop.handleBackdropOpen();
    try {
      const { data, error } = await supabase
        .schema('app')
        .from('merchants')
        .select('*');

      if (error) {
        console.error('Error fetching brands:', error);
        throw error;
      }

      setBrands(data);
    } catch (error) {
      const errorCast = error as Error;

      console.error(errorCast.stack);
      console.error(errorCast.name);
      snackbar.handleSnackbarOpen(String(error), 'error');
    }
    backdrop.handleBackdropClose();
  };

  useEffect(() => {
    getData();
  }, []);

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack spacing={3}>
        <Controller
          name="merchant_id"
          control={control}
          rules={{ required: 'Brand is required' }}
          render={({ field }) => (
            <TextField
              {...field}
              select
              label="Brand"
              error={!!errors.merchant_id}
              helperText={errors.merchant_id?.message}
              required
              fullWidth>
              {brands.map((brand) => (
                <MenuItem key={brand.id} value={brand.id}>
                  {brand.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
        <Controller
          name="user_role"
          control={control}
          rules={{ required: 'User role is required' }}
          render={({ field }) => (
            <TextField
              {...field}
              select
              label="User Role"
              error={!!errors.user_role}
              helperText={errors.user_role?.message}
              required
              fullWidth>
              {userRoles.map((userRole) => (
                <MenuItem key={userRole} value={userRole}>
                  {userRole}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
        <Controller
          name="email"
          control={control}
          rules={{ required: 'Email is required' }}
          render={({ field }) => (
            <TextField
              {...field}
              label="Email"
              error={!!errors.email}
              helperText={errors.email?.message}
              required
              fullWidth
            />
          )}
        />
        <Controller
          name="password"
          control={control}
          rules={{ required: 'Password is required' }}
          render={({ field }) => (
            <TextField
              {...field}
              type="password"
              label="Password"
              error={!!errors.password}
              helperText={errors.password?.message}
              required
              fullWidth
            />
          )}
        />
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={isSubmitting}>
          Create User
        </Button>
      </Stack>
    </Box>
  );
}
